// Core data types for MONOLOG LIVE

export type CommenterType =
	| "devils_advocate"
	| "ideaman"
	| "logical_thinking"
	| "facilitator"
	| (string & {});

/**
 * Definition for a commenter including display metadata
 */
export interface CommenterDefinition {
	id: CommenterType;
	name: string;
	description: string;
	icon: string;
	color: string;
}

/**
 * Default commenter definitions
 */
export const DEFAULT_COMMENTER_DEFINITIONS: CommenterDefinition[] = [
	{
		id: "devils_advocate",
		name: "悪魔の代弁者",
		description:
			"議論の妥当性を検証するため、あえて少数意見や反対意見を述べる役割。",
		icon: "😈",
		color: "#5a2d2d",
	},
	{
		id: "ideaman",
		name: "アイデアマン",
		description:
			"豊かな発想力で自由奔放に新しい着想や解決策などを生み出します。",
		icon: "💡",
		color: "#5a4d2d",
	},
	{
		id: "logical_thinking",
		name: "ロジカルシンキング",
		description:
			"様々なフレームワークや手法を用いて、論理的・構造的思考を行います。",
		icon: "🧠",
		color: "#2d4d5a",
	},
	{
		id: "facilitator",
		name: "ファシリテーター",
		description: "中立的な立場から、相互理解や合意形成を促進する進行役。",
		icon: "⚖️",
		color: "#2d5a4d",
	},
];

export type UserInteractionType = "pin";

export type AudioFormat = "webm" | "mp4" | "wav";

export type RecordingStatus = "idle" | "recording" | "paused" | "error";

export interface Comment {
	id: string;
	commenter: CommenterType;
	content: string;
	timestamp: Date;
	context: ConversationContext;
	userInteraction?: UserInteraction;
}

export interface ConversationContext {
	recentTranscript: string;
	silenceDuration: number;
	detectedEmotion?: string;
	silenceState?: {
		isInSilence: boolean;
		silenceDuration: number;
	};
	// Context compression and pinning (Phase 2)
	compressedHistory?: string;
	pinnedComments?: string[];
	// Properties used in tests and session tracking
	sessionDuration?: number;
	commentHistory?: Comment[];
	userEngagementLevel?: number;
	speechVolume?: number;
}

export interface UserInteraction {
	commentId: string;
	type: UserInteractionType;
	timestamp: Date;
	confidence: number;
}

export interface Session {
	id: string;
	userId: string;
	startTime: Date;
	endTime?: Date;
	title?: string;
	isFavorite?: boolean;
	isPinned?: boolean;
	transcript: TranscriptSegment[];
	comments: Comment[];
	interactions: UserInteraction[];
	metrics: SessionMetrics;
	compressedHistory?: string;
	pausedIntervals?: { start: Date; end?: Date }[];
}

export interface TranscriptSegment {
	id?: string;
	start: number;
	end: number;
	text: string;
	confidence: number;
	isFinal: boolean;
	type?: "speech" | "comment";
	commenterId?: string;
	isCompressed?: boolean;
}

export interface SessionMetrics {
	totalDuration: number;
	commentCount: number;
	interactionCount: number;
}

export interface UserPreferences {
	userId: string;
	lowConfidenceThreshold?: number;
	commentMaxCount?: number;
	commenterDefinitions?: CommenterDefinition[];
	policyConfig?: PolicyConfig;
	apiKeys?: ApiConfig;
	audioConfig?: Partial<AudioAnalysisConfig>;
	/** コメント生成完了時にブラウザ通知を送るかどうか */
	notificationsEnabled?: boolean;
	/** 自動コメント生成のタイミング制御設定 */
	autoCommentConfig?: Partial<AutoCommentConfig>;
}

export interface PolicyConfig {
	basicPolicy?: string;
}

export const DEFAULT_BASIC_POLICY =
	"あなたは、ライブ配信のチャットセクションを「知的な探索の場」へと変える、高度な知性を持ったオーディエンス・オーケストレーターです。\nユーザーの「セカンドブレイン」構築を支援するため、単なる相槌ではなく、ユーザーがハッとするような「新しい接続」や「未踏の視点」を提示してください。";

export interface ApiConfig {
	// Single Google AI Studio key shared across summary and comment generation.
	summaryApiKey?: string;
}

export interface AudioAnalysisConfig {
	volumeThreshold: number; // minimum volume to consider as speech
	silenceThreshold: number; // milliseconds before considering silence
	analysisInterval: number; // milliseconds between analysis updates
	smoothingFactor: number; // 0-1, higher = more smoothing
	autoResumeThreshold: number; // minimum volume to auto-resume from pause
}

export const DEFAULT_AUDIO_ANALYSIS_CONFIG: AudioAnalysisConfig = {
	volumeThreshold: 10, // 10% volume threshold
	silenceThreshold: 1000, // 1 second silence threshold
	analysisInterval: 100, // analyze every 100ms
	smoothingFactor: 0.3, // moderate smoothing
	autoResumeThreshold: 25, // 25% volume to auto-resume
};

/**
 * Configuration for automatic comment generation timing
 */
export interface AutoCommentConfig {
	/** 自動コメント生成を有効にするか */
	enabled: boolean;
	/** 沈黙をコメント候補とみなす閾値（ms）。デフォルト2500ms */
	silenceThresholdMs: number;
	/** 前回コメントからの最短インターバル（ms）。連続生成防止 */
	minIntervalMs: number;
	/** 前回コメントからの最長インターバル（ms）。強制生成の上限 */
	maxIntervalMs: number;
	/** 前回コメント以降に必要な最低新規テキスト文字数 */
	minNewChars: number;
}

/** デフォルトの自動コメント生成設定（デフォルトON） */
export const DEFAULT_AUTO_COMMENT_CONFIG: AutoCommentConfig = {
	enabled: true,
	silenceThresholdMs: 2500,
	minIntervalMs: 15000,
	maxIntervalMs: 60000,
	minNewChars: 30,
};

export interface ActivityEvent {
	type: "speech" | "comment" | "interaction" | "silence";
	timestamp: Date;
	data: unknown;
	duration?: number;
}

export interface AudioFile {
	id: string;
	sessionId: string;
	filename: string;
	format: AudioFormat;
	duration: number;
	size: number;
	createdAt: Date;
	quality: AudioQualitySettings;
	// Audio data properties for processing and export
	blob?: Blob;
	buffer?: ArrayBuffer;
	url?: string;
}

export interface AudioQualitySettings {
	bitrate: number;
	sampleRate: number;
	channels: number;
}

export interface StorageInfo {
	used: number;
	available: number;
	quota: number;
}

export interface SessionSummary {
	sessionId: string;
	overallSummary: string;
	generatedAt: Date;
	formattedTranscript?: string;
	topics?: string[];
	title?: string;
}
